import { useQuery } from '@tanstack/react-query';
import { FileQuestion } from 'lucide-react';
import { useContext, useMemo, useState } from 'react';
import { useTranslate } from 'sbx-crm-menu-ui';
import ButtonComponent from 'src/components/shared/ButtonComponent';
import NavbarBreadcrumbV2 from 'src/components/shared/NavbarBreadcrumbV2';
import { Textarea } from 'src/components/ui/textarea';
import { AppAgentContext } from 'src/context/ContextProvider';
import { getAgentByIdServiceV2, getAllAgentsServiceV2 } from 'src/services/AgentService';
import { analyzeDocumentOrImageService } from 'src/services/DocumentOrImageV2Service';
import { SkillType } from 'src/types/AiAgentV2';
import { SelectComponent } from 'src/utils/UtilsComponent';
import { Card, CardContent, CardTitle } from '../../ui/card';
import { useNavigate } from 'react-router-dom';
import iziToast from 'izitoast';
import Permission from 'src/components/shared/Permission';
import { MARKETING_V2_CREATE } from 'src/types/Permission';
import { DeniedComponent } from 'src/App';

const classNameLabel = 'form-input-label';
const QuestionMarketingAgentComponentV2 = () => {
    const {
        state: {
            session: { user_id },
        },
    } = useContext(AppAgentContext);
    const [agentSelected, setAgentSelected] = useState(0);
    const [question, setQuestion] = useState('');
    const [loading, setLoading] = useState(false);
    const { t: agent } = useTranslate('agent');
    const navigate = useNavigate();
    const { data: agents } = useQuery({
        queryKey: ['agents'],
        queryFn: async () =>
            await getAllAgentsServiceV2().then((res) =>
                res.items.map((item) => ({
                    label: item.name,
                    value: item.id ?? 1,
                }))
            ),
    });

    const { data: agentsWithSkills } = useQuery({
        queryKey: ['agentsWithSkills'],
        queryFn: async () =>
            await Promise.all(
                (agents || []).map(async (agent) => {
                    return (await getAgentByIdServiceV2(`${agent.value}`)).item;
                })
            ),
        enabled: (agents || []).length > 0,
    });

    const agentsFilter = useMemo(() => {
        if (agentsWithSkills) {
            const data = agentsWithSkills.filter((ag) =>
                ag.skills?.some((at) => at.skill_type === SkillType.SkillContentGenerator)
            );
            if (data.length > 0) {
                return data.map((item) => ({
                    label: item.name,
                    value: item.id ?? 1,
                }));
            }
        }
        return [];
    }, [agentsWithSkills]);

    const questionToAgent = async () => {
        setLoading(true);

        const res = await analyzeDocumentOrImageService({
            agent_id: agentSelected,
            user: user_id,
            question,
            test: false,
            history: false,
            key_filter: 'content_generator',
            session_id: new Date().getTime().toString(),
        });

        if (res?.success) {
            iziToast.success({
                message: agent('creating_response_marketing_agent'),
                position: 'topCenter',
            });
            setLoading(false);
            navigate('/marketing-agent-v2/list');
        } else {
            setLoading(false);
        }
    };

    return (
        <Permission permission={MARKETING_V2_CREATE} denied={<DeniedComponent />}>
            <div className="min-h-screen bg-gradient-to-br from-purple-100 via-pink-100 to-blue-100 p-4 sm:p-8">
                <NavbarBreadcrumbV2
                    routes={[
                        {
                            label: agent('marketing_agent_list'),
                            route: '/marketing-agent-v2/list',
                        },
                        {
                            label: agent('marketing_agent'),
                            route: '',
                        },
                    ]}
                />

                <h1 className="title">{agent('marketing_agent')}</h1>
                <Card className="w-full max-w-4xl  mx-auto bg-white bg-opacity-90 backdrop-blur-lg rounded-3xl shadow-2xl overflow-hidden">
                    <CardContent className="p-6 grid grid-cols-1 gap-4">
                        <div>
                            <div className="mb-4">
                                <CardTitle className="text-lg font-semibold text-purple-700 my-4">
                                    {agent('agent')}
                                </CardTitle>
                                <SelectComponent
                                    value={agentSelected}
                                    onChange={(value) => {
                                        setAgentSelected(value);
                                    }}
                                    options={agentsFilter}
                                    classNameLabel={classNameLabel}
                                    required={true}
                                />
                            </div>
                            <div>
                                <CardTitle className="text-lg font-semibold text-purple-700 my-4">
                                    {agent('ask_a_question')}
                                </CardTitle>
                                <Textarea
                                    value={question}
                                    onChange={(e) => setQuestion(e.target.value)}
                                    placeholder={agent('ask_a_question')}
                                    onKeyDown={(event) => {
                                        if (event.key === 'Enter') {
                                            event.preventDefault();
                                            questionToAgent();
                                        }
                                    }}
                                    className="w-full border-2 border-purple-300 focus:border-purple-500 rounded-xl py-2 px-4 resize-none"
                                    rows={3}
                                />
                            </div>
                            <ButtonComponent
                                loading={loading}
                                disabled={loading || question.trim().length === 0 || agentSelected === 0}
                                onClick={questionToAgent}
                                label={
                                    <>
                                        <FileQuestion size={20} className="mr-2" /> {agent('ask_a_question')}
                                    </>
                                }
                                className="mt-5 primary-gradient-button text-white font-semibold py-2 px-4 rounded-full"
                            />
                        </div>
                    </CardContent>
                </Card>
            </div>
        </Permission>
    );
};

export default QuestionMarketingAgentComponentV2;
